import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import {
  generateProductVersionDiff,
  updateProductsWithReferences,
  type VersionDiffAppointment,
  type VersionDiffSetting,
  type VersionDiffTeachings,
} from '@adornis/buchungsheld-db/api/product.js';
import { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import type { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { CertificateAttachedProduct } from '@adornis/buchungsheld-db/entities/products.js';
import {
  ProductUpdateWithReferences,
  type ProductUpdateWithReferencesNarrEnt,
} from '@adornis/buchungsheld-db/entities/ProductUpdateWithReferences.js';
import { Setting } from '@adornis/buchungsheld-db/entities/settings.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-currency-input.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-formfield-wrapper.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/components/tr-tinymce.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import { Price } from '@adornis/payments/db/prices.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip';
import { goTo } from '@adornis/router/client/open-href.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime, Duration } from 'luxon';
import { combineLatest, filter, from, map, shareReplay, switchMap, tap } from 'rxjs';
import '../../../../components/bh-admin-product-entry-requirements-overview.js';
import type { BhAdminProductEntryRequirementsOverview } from '../../../../components/bh-admin-product-entry-requirements-overview.js';
import '../../../../components/bh-admin-product-waitinglist-priority-overview.js';
import type { BhAdminProductWaitingListPriorityOverview } from '../../../../components/bh-admin-product-waitinglist-priority-overview.js';
import '../../../../components/bh-admin-teaching-overview';
import { BhAdminTeachingOverview } from '../../../../components/bh-admin-teaching-overview.js';
import { BHFormController } from '../../../../controller/BHFormController.js';
import './../../../../components/bh-category-picker.js';
import './../../../../components/bh-product-certificate-picker.js';
import { BHPromptAppointmentCreate } from './../../../../prompts/bh-prompt-appointment-create.js';

@customElement('bh-page-admin-einrichtung-workshops-modify')
export class BHPageAdminEinrichtungWorkshopsModify extends BuchungsheldLitElement {
  @property({ attribute: 'workshop-id' }) workshopID = new RXController<string | null>(this, null);
  @property({ attribute: 'mode' }) mode: 'add' | 'edit' | undefined = undefined;
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  orginalData: { workshop: PartialEntityData<Workshop>; setting?: PartialEntityData<Setting> } | undefined;
  @state() private readonly workshop = new RXController(
    this,
    combineLatest([this.institutionID.observable, this.workshopID.observable]).pipe(
      filter(([institutionID]) => Boolean(institutionID)),
      switchMap(([institutionID, workshopID]) => {
        institutionID = institutionID as string;
        if (workshopID)
          return subscribeByID(
            Workshop,
            workshopID,
          )({
            ...Workshop.allFields, // TODO: only reuqest non resolved fields. But saving Currently requires resolved fields for some reason
            appointments: selectionSet(() => Appointment, 2),
            attachedCertificate: selectionSet(() => CertificateAttachedProduct, 4),
            // ...versionDiffWorkshopSelSet,
          });

        return from(Setting.getNewProductSetting(institutionID)(selectionSet(() => Setting, 4))).pipe(
          map(settings => new Workshop({ _id: workshopID, institutionID, settings })),
        );
      }),
      filter(isDefined),
      tap(workshop => {
        this.orginalData = { workshop: workshop.toObject(), setting: workshop.settings!.toObject() };
        // add priceGroup prices (in case they dont exist)
        const paymentPlugin = getPluginForScope('payment', workshop.institutionID);
        const firstPrice = workshop.prices[0]; // should always exist
        for (const [i, _priceGroup] of Object.entries(paymentPlugin.priceGroups)) {
          workshop.prices[i] = workshop.prices[i] ?? new Price({ ...firstPrice, _id: A.getGloballyUniqueID() });
        }

        // set defaults
        if (!workshop.presenceType) workshop.presenceType = 'not_selected';
      }),
      shareReplay(1),
    ),
  );

  /*   @state() private readonly _oldWorkshop = new RXController(
    this,
    combineLatest([this.institutionID.observable, this.workshopID.observable]).pipe(
      filter(([institutionID]) => Boolean(institutionID)),
      switchMap(async ([institutionID, workshopID]) => {
        institutionID = institutionID as string;
        if (workshopID) return (await getByID(Workshop, workshopID)(Workshop.allFields)) as Workshop;
        const settings = await Setting.getNewProductSetting(institutionID)(Setting.allFields);

        return new Workshop({ _id: workshopID, institutionID, settings });
      }),
      map(workshop => workshop.doc),
    ),
  );
 */
  @state() private readonly _timeframes = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => Timeframe.getTimeframesByInstitution(institutionID)(Timeframe.allFields)),
    ),
  );

  @state() private readonly _workshopController = new BHFormController(
    this,
    this.workshop.observable.pipe(filter(Boolean)),
    {
      error: err => {
        const message = (() => {
          if (err.extensions.translationKey === 'validation_non_optional') {
            logger.error({ error: err }, 'validation_non_optional');
            return this._translation.translate('validation_required_fields_missing');
          }

          if (err.message.includes('ValidationError') || err instanceof ValidationError) {
            return this._translation.translate('check_form');
          }

          return (err as any).message;
        })();

        return XSnackbar.show(message);
      },
      success: async dirtyProduct => {
        try {
          // sync settings and workshop-capacity
          dirtyProduct.settings.capacity = dirtyProduct.capacity;

          const dirtyProdRefs = new ProductUpdateWithReferences({
            product: dirtyProduct,
            teachings: this._teachingOverview.value?.dirtyTeachingList as VersionDiffTeachings[],
            setting: dirtyProduct.settings as VersionDiffSetting,
            appointments: dirtyProduct?.appointments as VersionDiffAppointment[],
          }) as ProductUpdateWithReferencesNarrEnt;

          if (this.mode === 'add') {
            const productID = await dirtyProduct.create();
            dirtyProduct._id = productID;
            dirtyProduct.settings.productID = productID;

            await dirtyProduct.settings.save();
            await this._saveTeachings();
          }

          if (this.mode === 'edit') {
            let notifyParticipantsAboutChanges = false;
            const participationsCount = dirtyProduct.capacity - dirtyProduct.leftCapacity;
            // Hole alle Änderungen aus dem docMeta
            const productChanges = await generateProductVersionDiff(
              new ProductUpdateWithReferences({
                product: new Workshop(this.orginalData!.workshop),
                teachings: this._teachingOverview.value?.appliedTeachingList as VersionDiffTeachings[],
                setting: this.orginalData?.setting
                  ? (new Setting(this.orginalData.setting) as VersionDiffSetting)
                  : null,
                appointments: this.workshop.value?.appointments as VersionDiffAppointment[],
              }) as ProductUpdateWithReferencesNarrEnt,
              dirtyProdRefs,
            );
            if (participationsCount > 0 && Object.keys(productChanges).length > 0) {
              const question = this._translation.translate('WORKSHOPS', 'inform_participants_conf');
              const doSendMails = await TrDialog.confirm(question);
              notifyParticipantsAboutChanges = doSendMails;
            }
            await updateProductsWithReferences(dirtyProdRefs, notifyParticipantsAboutChanges)();
          }

          // TODO: save everething with 'updateProductWithReferences'
          this._saveEntryRequirements();
          this._saveWaitingListPriority();

          dirtyProduct.revert();
          void XSnackbar.show(this._translation.translate('saved') + '!');
          this.dispatchEvent(new CustomEvent('saved', { detail: { workshopID: dirtyProduct._id } }));
        } catch (e) {
          console.trace('hey');
          logger.error({ error: e }, 'validation error');
          void XSnackbar.show(`${this._translation.translate('error')}: ${(e as Error).message}`);
          throw e;
        }
      },
    },
  );

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.workshop.value?.revert();
  }

  override render() {
    const workshop = this._workshopController.document;
    const institutionID = this.institutionID.value;
    if (!institutionID || !workshop) return html`<x-loading-state></x-loading-state>`;

    const settings = workshop.settings;
    const showInherited = (iv: [string, string | number | boolean] | undefined) => {
      return iv
        ? `${this._translation.translate('settings', 'defaults', iv[0] as unknown as undefined)}: ${iv[1]}`
        : '';
    };

    const paymentPlugin = getPluginForScope('payment', institutionID);
    const availablePaymentMethods = paymentPlugin.paymentMethods;
    if (!workshop.settings.paymentMethods) {
      // @ts-expect-error set default to everthing activated
      this._workshopController.document.settings.doc.paymentMethods = availablePaymentMethods;
    }

    const firstAppointment = workshop.appointments.reduce<Appointment | null>((acc, curr) => {
      if (!acc || curr.start < acc.start) return curr;
      return acc;
    }, null);

    this.exposeAction('save', {
      icon: 'save',
      text: this._translation.translate('save'),
      callback: () => this.saveWorkshop(),
    });

    if (this.mode === 'edit')
      this.exposeAction('delete', {
        icon: 'delete',
        color: 'danger',
        callback: () => this.deleteWorkshop(),
      });

    return html`
      <x-flex space="md">
        <x-flex space="md" horizontal>
          <tr-input
            flex
            ${this._workshopController.field('name')}
            placeholder="${this._translation.translate('field', 'name')}*"
          ></tr-input>
          <tr-input
            type="number"
            ${this._workshopController.field('sortingWeight')}
            placeholder=${this._translation.translate('field', 'sortingWeight')}
            info=${this._translation.translate('fieldInfo', 'sortingWeight')}
          ></tr-input>
        </x-flex>

        <bh-admin-multi-lang-text
          label="${this._translation.translate('field', 'description')}*"
          ${this._workshopController.field('translatedDescriptionHTML')}
        ></bh-admin-multi-lang-text>

        <x-input
          type="date"
          ${this._workshopController.field('publishDate')}
          placeholder="${this._translation.translate('field', 'publishDate')}"
        ></x-input>

        <tr-input
          flex
          type="text"
          ${this._workshopController.field('topic')}
          placeholder="${this._translation.translate('field', 'topic')}"
        ></tr-input>

        <x-dropdown-selection
          ${this._workshopController.field('presenceType')}
          placeholder=${this._translation.translate('PRODUCT', 'presenceType')}
          .selectables=${['not_selected', 'in_person', 'online', 'hybrid'] as any}
          .renderString=${item => this._translation.translate('PRODUCT', 'presenceTypes', item)}
        ></x-dropdown-selection>

        <x-dropdown-selection
          ${this._workshopController.field('language')}
          placeholder=${this._translation.translate('PRODUCT', 'language')}
          .selectables=${['de', 'en', null] as any}
          .renderString=${item => (!item ? '----' : this._translation.translate('PRODUCT', 'languages', item))}
        ></x-dropdown-selection>

        <x-input
          type="number"
          ${this._workshopController.field('capacity')}
          step="1"
          placeholder="${this._translation.translate('field', 'capacity')}*"
        ></x-input>

        <x-checkbox
          ${this._workshopController.field('hideCapacity')}
          .label=${this._translation.translate('workshop', 'hide_capacity')}
        ></x-checkbox>

        <x-flex horizontal crossaxis-center space="md">
          <x-checkbox
            label=${this._translation.translate('field', 'hasPrice')}
            ${this._workshopController.field('hasPrice')}
            @value-picked=${(e: ValueEvent<boolean>) => {
              if (e.detail.value && this._workshopController.document?.prices[0]?.unitAmount) {
                this._workshopController.document.prices[0].unitAmount = 0;
              }
              this.requestUpdate();
            }}
          ></x-checkbox>
          ${this._workshopController.document?.hasPrice
            ? html`
                ${paymentPlugin.priceGroups.map(
                  (priceGroup, i) => html`
                    <tr-formfield-wrapper
                      placeholder=${this._translation.translate('field', 'unitAmount') +
                      (paymentPlugin.priceGroups.length > 1 ? ` (${priceGroup})` : '')}
                    >
                      <tr-currency-input
                        ${this._workshopController.field('prices', i)}
                        default=${0}
                      ></tr-currency-input>
                    </tr-formfield-wrapper>
                  `,
                )}
                ${availablePaymentMethods.length > 1
                  ? html` <x-dropdown-multiselection
                      ${this._workshopController.field('settings', 'paymentMethods')}
                      placeholder=${this._translation.translate('PAYMENT', 'method')}
                      .selectables=${availablePaymentMethods}
                      .renderString=${item => this._translation.translate('PAYMENT', 'methods', item)}
                    ></x-dropdown-multiselection>`
                  : nothing}
              `
            : nothing}
        </x-flex>

        <!-- categories -->
        <bh-category-picker
          flex
          institution-id=${institutionID}
          .value=${this._workshopController.document?.category}
          @value-picked=${(e: ValueEvent<Maybe<Category>>) => {
            const doc = this._workshopController.document;
            if (!doc) throw new Error('form not yet initialized');
            const category = e.detail.value;
            // TODO category shouldn't be required but it is atm
            if (!category) return;
            doc.category = category;
            doc.categoryID = category._id;
            this.requestUpdate();
          }}
        >
        </bh-category-picker>

        <!-- timeframe -->
        <x-dropdown-selection
          clearable
          .emptyState=${this._translation.translate('dropdowns', 'emptyStateTimeframes')}
          placeholder=${this._translation.translate('timeframe', 'singular')}
          ${this._workshopController.field('timeframeID')}
          .selectables=${this._timeframes.value?.map(timeframe => timeframe._id) ?? []}
          .renderString=${(instanceID: string) =>
            this._timeframes.value?.find(timeframe => timeframe._id === instanceID)?.name ?? ''}
        >
        </x-dropdown-selection>

        <!-- certificate -->
        <tr-flex>
          <bh-product-certificate-picker
            institution-id=${this.institutionID.value}
            ${this._workshopController.field('attachedCertificate')}
          ></bh-product-certificate-picker>
        </tr-flex>

        <!-- participation Constraints -->
        <tr-input
          flex
          type="text"
          .value=${this.workshop.value?.settings.extraParticipationConstraints?.join(', ')}
          @value-picked=${e => {
            const value = e.detail.value || null;
            this._workshopController.document!.settings.extraParticipationConstraints = value
              ?.replace(/, */g, ',') // remove whitespaces after ','
              .split(',');
          }}
          placeholder="${this._translation.translate('field', 'extraParticipationConstraints')}"
          info=${this._translation.translate('settings', 'extraParticipationConstraintsExpl')}
          realPlaceholder="e.g. Kriterium 1, Kriterium 2"
        ></tr-input>

        <tr-input
          ${this._workshopController.field('settings', 'courseLocation')}
          type="text"
          placeholder=${this._translation.translate('settings', 'course_location')}
          realPlaceholder=${showInherited(settings.inheritedCourseLocation)}
        ></tr-input>
        <tr-input
          ${this._workshopController.field('settings', 'blockSignOffDaysInAdvance')}
          type="number"
          step="1"
          placeholder=${this._translation.translate('settings', 'cancel_deadline')}
          realPlaceholder=${showInherited(settings.inheritedBlockSignOffDaysInAdvance)}
          @value-picked=${() => this.requestUpdate()}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'cancellationDeadline')} ${
            firstAppointment?.start &&
            (workshop.settings.blockSignOffDaysInAdvance ?? workshop.settings.inheritedBlockSignOffDaysInAdvance?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'cancellationDeadlineDateInfo',
                )} ${
                  firstAppointment.start
                    .minus({
                      days:
                        workshop.settings.blockSignOffDaysInAdvance ??
                        workshop.settings.inheritedBlockSignOffDaysInAdvance![1]!,
                    })
                    .toLocaleString(DateTime.DATETIME_SHORT) ?? ''
                }`
              : ''
          }`}
        ></tr-input>

        <tr-input
          ${this._workshopController.field('settings', 'blockAutomaticMoveUpDaysInAdvance')}
          type="number"
          step="1"
          placeholder=${this._translation.translate('settings', 'move_up_notice')}
          realPlaceholder=${showInherited(settings.inheritedBlockAutomaticMoveUpDaysInAdvance)}
          @value-picked=${() => this.requestUpdate()}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'waitinglistDeadline')} ${
            firstAppointment?.start &&
            (workshop.settings.blockAutomaticMoveUpDaysInAdvance ??
              workshop.settings.inheritedBlockAutomaticMoveUpDaysInAdvance?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'waitingListDeadlineDateInfo',
                )} ${
                  firstAppointment.start
                    .minus({
                      days:
                        workshop.settings.blockAutomaticMoveUpDaysInAdvance ??
                        workshop.settings.inheritedBlockAutomaticMoveUpDaysInAdvance![1]!,
                    })
                    .toLocaleString(DateTime.DATETIME_SHORT) ?? ''
                }`
              : ''
          }`}
        ></tr-input>

        <!-- auto nachruecken -->
        ${this._workshopController.document?.hasPrice ?? true
          ? nothing
          : html`
              <x-checkbox
                ${this._workshopController.field('settings', 'autoNachrueckenEnabled')}
                .label=${this._translation.translate('workshop', 'auto_nachruecken')}
              ></x-checkbox>
            `}

        <tr-input
          type="number"
          step="1"
          @value-picked=${() => this.requestUpdate()}
          placeholder=${this._translation.translate('settings', 'reminder_mail_1')}
          realPlaceholder=${showInherited(settings.inheritedFirstReminderLeadTime)}
          ${this._workshopController.field('settings', 'firstReminderLeadTime')}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'firstReminderMail')} ${
            workshop.appointments?.length &&
            (workshop.settings.firstReminderLeadTime ?? workshop.settings.inheritedFirstReminderLeadTime?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'firstReminderMailDateInfo',
                )} <br />${(() => {
                  const start = workshop.appointments?.[0]?.start;
                  if (!start) return '';
                  return `${start
                    .minus({
                      days:
                        workshop.settings.firstReminderLeadTime ??
                        workshop.settings.inheritedFirstReminderLeadTime![1]!,
                    })
                    .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`;
                })()}`
              : ''
          }`}
        ></tr-input>
        <tr-input
          type="number"
          step="1"
          @value-picked=${() => this.requestUpdate()}
          placeholder=${this._translation.translate('settings', 'reminder_mail_2')}
          realPlaceholder=${showInherited(settings.inheritedSecondReminderLeadTime)}
          ${this._workshopController.field('settings', 'secondReminderLeadTime')}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')} ${
            workshop.appointments?.length &&
            (workshop.settings.secondReminderLeadTime ?? workshop.settings.inheritedSecondReminderLeadTime?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'secondReminderMailDateInfo',
                )} <br />${(() => {
                  const start = workshop.appointments?.[0]?.start;
                  if (!start) return '';
                  return `${start
                    .minus({
                      days:
                        workshop.settings.secondReminderLeadTime ??
                        workshop.settings.inheritedSecondReminderLeadTime![1]!,
                    })
                    .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`;
                })()}`
              : ''
          }`}
        ></tr-input>

        <!-- appointment reminder -->
        <tr-input
          type="number"
          step="1"
          @value-picked=${() => this.requestUpdate()}
          placeholder=${this._translation.translate('settings', 'appointment_reminder_mail_1')}
          realPlaceholder=${showInherited(settings.inheritedFirstAppointmentReminderLeadTime)}
          ${this._workshopController.field('settings', 'firstAppointmentReminderLeadTime')}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'firstReminderMail')} ${
            workshop.appointments?.length &&
            (workshop.settings.firstAppointmentReminderLeadTime ??
              workshop.settings.inheritedFirstAppointmentReminderLeadTime?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'firstAppointmentReminderMailDateInfo',
                )} <br />${workshop.appointments
                  .filter(appointment => appointment.start)
                  .map(
                    appointment =>
                      `${appointment
                        .start!.minus({
                          days:
                            workshop.settings.firstAppointmentReminderLeadTime ??
                            workshop.settings.inheritedFirstAppointmentReminderLeadTime![1]!,
                        })
                        .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`,
                  )
                  .join('<br />')}`
              : ''
          }`}
        ></tr-input>
        <tr-input
          type="number"
          step="1"
          @value-picked=${() => this.requestUpdate()}
          placeholder=${this._translation.translate('settings', 'appointment_reminder_mail_2')}
          realPlaceholder=${showInherited(settings.inheritedSecondAppointmentReminderLeadTime)}
          ${this._workshopController.field('settings', 'secondAppointmentReminderLeadTime')}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'secondAppointmentReminderMail')} ${
            workshop.appointments?.length &&
            (workshop.settings.secondAppointmentReminderLeadTime ??
              workshop.settings.inheritedSecondAppointmentReminderLeadTime?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'secondAppointmentReminderMailDateInfo',
                )} <br />${workshop.appointments
                  .filter(appointment => appointment.start)
                  .map(
                    appointment =>
                      `${appointment
                        .start!.minus({
                          days:
                            workshop.settings.secondAppointmentReminderLeadTime ??
                            workshop.settings.inheritedSecondAppointmentReminderLeadTime![1]!,
                        })
                        .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`,
                  )
                  .join('<br />')}`
              : ''
          }`}
        ></tr-input>

        <tr-input
          type="number"
          step="1"
          placeholder=${this._translation.translate('settings', 'appointment_info_mail')}
          realPlaceholder=${showInherited(settings.inheritedMeetingInfoMailLeadTime)}
          ${this._workshopController.field('settings', 'meetingInfoMailLeadTime')}
          info=${`${this._translation.translate('fieldInfo', 'institution', 'meetingInfoMailLeadTime')} ${
            workshop.appointments?.length &&
            (workshop.settings.meetingInfoMailLeadTime ?? workshop.settings.inheritedMeetingInfoMailLeadTime?.[1])
              ? `<br /> <br /> ${this._translation.translate(
                  'fieldInfo',
                  'institution',
                  'meetingInfoMailLeadTime',
                )} <br />${workshop.appointments
                  .map(
                    appointment =>
                      `${appointment.start
                        ?.minus({
                          days:
                            workshop.settings.meetingInfoMailLeadTime ??
                            workshop.settings.inheritedMeetingInfoMailLeadTime![1]!,
                        })
                        .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`,
                  )
                  .join('<br />')}`
              : ''
          }`}
        >
        </tr-input>

        <!-- infoLink -->
        <tr-input
          ${this._workshopController.field('settings', 'infoLink')}
          placeholder="Infolink"
          realPlaceholder=${showInherited(settings.inheritedInfoLink)}
        ></tr-input>

        <!-- evaluationLink -->
        <tr-input
          ${this._workshopController.field('settings', 'evaluationLink')}
          placeholder="Evaluation Link"
          realPlaceholder=${showInherited(settings.inheritedEvaluationLink)}
        ></tr-input>

        <!-- evaluationLink code -->
        <tr-input
          ${this._workshopController.field('settings', 'evaluationLinkCode')}
          placeholder="Evaluation Code"
          realPlaceholder=${showInherited(settings.inheritedEvaluationLinkCode)}
        ></tr-input>

        <!-- qrCode valiidity -->
        <x-flex horizontal crossaxis-center space="md">
          <tr-input
            flex
            type="number"
            step="1"
            ${this._workshopController.field('settings', 'qrCodeValidityInMinutes')}
            placeholder=${this._translation.translate('field', 'qrcode_valid')}
            realPlaceholder=${showInherited(settings.inheritedQrCodeValidityInMinutes)}
            postfix="Min."
            @value-picked=${() => this.requestUpdate() /* trigger update of readable duration preview */}
          ></tr-input>
          ${(() => {
            const set = this._workshopController.document?.settings;
            const value = set?.qrCodeValidityInMinutes ?? set?.inheritedQrCodeValidityInMinutes?.[1];
            if (!set) return nothing;
            return html`
              <x-text tone="subtle">
                ${Duration.fromObject({ minutes: value }).toFormat(
                  `dd' ${this._translation.translate('days')}, 'hh' ${this._translation.translate(
                    'hours',
                  )} und 'mm' ${this._translation.translate('minutes')}'`,
                )}
              </x-text>
            `;
          })()}
        </x-flex>

        ${workshop.hasPrice &&
        getPluginForScope('payment', this.institutionID.value)?.paymentMethods.includes('invoice') &&
        firstAppointment?.start
          ? html`
              <tr-input
                type="email"
                info=${this._translation.translate('settings', 'internAccountingEmailDesc')}
                placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 1'}
                realPlaceholder=${showInherited(settings.inheritedInterAccountingEmail1)}
                ${this._workshopController.field('settings', 'interAccountingEmail1')}
              ></tr-input>

              <tr-input
                type="email"
                info=${this._translation.translate('settings', 'internAccountingEmailDesc')}
                placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 2'}
                realPlaceholder=${showInherited(settings.inheritedInterAccountingEmail2)}
                ${this._workshopController.field('settings', 'interAccountingEmail2')}
              ></tr-input>

              <tr-input
                type="email"
                info=${this._translation.translate('settings', 'internAccountingEmailDesc')}
                placeholder=${this._translation.translate('settings', 'internAccountingEmail') + ' 3'}
                realPlaceholder=${showInherited(settings.inheritedInterAccountingEmail3)}
                ${this._workshopController.field('settings', 'interAccountingEmail3')}
              ></tr-input>

              <tr-input
                type="number"
                step="1"
                ${this._workshopController.field('settings', 'sendInvoiceDaysInAdvance')}
                placeholder=${this._translation.translate('settings', 'sendInvoiceDaysInAdvance')}
                realPlaceholder=${showInherited(settings.inheritedSendInvoiceDaysInAdvance)}
                info=${`${this._translation.translate('fieldInfo', 'institution', 'sendInvoiceDaysInAdvance')} ${
                  firstAppointment &&
                  (workshop.settings.sendInvoiceDaysInAdvance ??
                    workshop.settings.inheritedSendInvoiceDaysInAdvance?.[1])
                    ? `<br /> <br /> ${this._translation.translate(
                        'fieldInfo',
                        'institution',
                        'sendInvoiceDaysInAdvanceDateInfo',
                      )} ${firstAppointment.start
                        .minus({
                          days:
                            workshop.settings.sendInvoiceDaysInAdvance ??
                            workshop.settings.inheritedSendInvoiceDaysInAdvance![1]!,
                        })
                        .toLocaleString(DateTime.DATE_SHORT)}, ~ 1 Uhr`
                    : ''
                }`}
              ></tr-input>
            `
          : nothing}

        <!-- auto cerfication mails -->
        <x-checkbox
          ${this._workshopController.field('settings', 'autoSendCertifatesEnabled')}
          .label=${this._translation.translate('workshop', 'auto_send_certificates')}
        ></x-checkbox>

        <!-- lecturers -->
        ${this.renderLecturerPart()}

        <!-- product entry requirements -->
        ${this.renderProductEntryRequirementsPart()}

        <!-- waiting-list priority -->
        ${this.renderWaitingListPriorityPart()}

        <!-- appointments -->
        <x-flex space="md">
          <br />
          <x-h3> ${this._translation.translate('field', 'appointments')} </x-h3>

          <x-flex horizontal space="md" wrap>
            ${workshop.appointments.map(
              (appointment, index) => html`
                <x-card ${css({ flexBasis: '260px', position: 'relative' })} space="sm">
                  <x-flex
                    @click=${() => {
                      workshop.appointments.splice(index, 1);
                      this.requestUpdate();
                    }}
                    ${css({
                      position: 'absolute',
                      padding: this.spacing.sm,
                      borderRadius: '50%',
                      right: '-5px',
                      top: '-5px',
                      height: '20px',
                      width: '20px',
                      cursor: 'pointer',
                      background: this.colors.tone.error.shade(300),
                    })}
                    center
                    crossaxis-center
                  >
                    <x-icon ${css({ color: '#fff' })}> close </x-icon>
                  </x-flex>
                  <x-flex horizontal crossaxis-end space="xs" })}>
                    <x-flex space="sm" flex>
                      <x-flex horizontal crossaxis-center space="sm">
                        ${!appointment.isNonBlocking ? html` <x-icon> how_to_reg </x-icon> ` : nothing}
                        ${appointment.name
                          ? html` <tr-text bold> ${appointment.name} </tr-text> `
                          : html`
                              <tr-text> ${index + 1}. ${this._translation.translate('termin', 'singular')} </tr-text>
                            `}
                      </x-flex>
                      ${(() => {
                        if (!appointment.start)
                          return html`
                            <x-flex horizontal space="sm" crossaxis-center>
                              <x-icon> update </x-icon>
                              <x-text> <i> TBD </i> </x-text>
                            </x-flex>
                          `;

                        return html`
                          <x-flex horizontal space="sm" crossaxis-center>
                            <x-icon> update </x-icon>
                            <x-text> ${appointment.start?.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
                          </x-flex>
                          <x-flex horizontal space="sm" crossaxis-center>
                            <x-icon> history </x-icon>
                            <x-text> ${appointment.end?.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
                          </x-flex>
                        `;
                      })()}
                      <x-flex horizontal space="sm" crossaxis-center>
                        <x-icon> location_on </x-icon>
                        <x-text> ${appointment.location ?? '---'} </x-text>
                      </x-flex>
                    </x-flex>
                    <tr-button-small
                      icon="edit"
                      mode="outline"
                      .color=${this.colors.secondary.shade(300)}
                      .action=${async () => {
                        await BHPromptAppointmentCreate.editOnWorkshop(appointment, workshop);
                        this.requestUpdate();
                      }}
                    ></tr-button-small>
                  </x-flex>
                </x-card>
              `,
            )}
            <x-card
              center
              crossaxis-center
              ${css({ flexBasis: '150px', cursor: 'pointer' })}
              href
              @click=${async () => {
                const dateMin = workshop.timeframe?.from;
                const dateMax = workshop.timeframe?.till;
                await BHPromptAppointmentCreate.addOnWorkshop(workshop, dateMin, dateMax);
                this.requestUpdate();
              }}
            >
              <x-icon> add </x-icon>
            </x-card>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  // lecturers section stuff
  private _teachingOverview = createRef<BhAdminTeachingOverview>();
  private renderLecturerPart() {
    return html`
      <br />
      <bh-admin-teaching-overview
        ${ref(this._teachingOverview)}
        institution-id=${this.institutionID.value}
        product-id=${this.workshopID.value}
      ></bh-admin-teaching-overview>
    `;
  }

  private _saveTeachings() {
    return this._teachingOverview.value?.saveAllTeachings(this._workshopController.document?._id);
  }

  // entryRequirement sections stuff
  private _productEntryRequirementsOverview = createRef<BhAdminProductEntryRequirementsOverview>();
  private renderProductEntryRequirementsPart() {
    return html`
      <br />
      <bh-admin-product-entry-requirements-overview
        ${ref(this._productEntryRequirementsOverview)}
        institution-id=${this.institutionID.value}
        product-id=${this.workshopID.value}
      ></bh-admin-product-entry-requirements-overview>
    `;
  }

  private _saveEntryRequirements() {
    return this._productEntryRequirementsOverview.value?.saveAllEntryRequirements(
      this._workshopController.document?._id,
    );
  }

  // waiting-list-priority stuff
  private _waitingListPriorityOverview = createRef<BhAdminProductWaitingListPriorityOverview>();
  private renderWaitingListPriorityPart() {
    const institutionID = this.institutionID.value;
    if (!institutionID) return nothing;

    const priorityPlugin = getPluginForScope('registration::product-waitinglist-priority-rules', institutionID);
    const kinds = priorityPlugin.getRuleKinds();
    if (kinds.length === 0) return nothing;

    const checkboxLabel =
      this._translation.translate('override') +
      ' Institution ' +
      this._translation.translate('productWaitinglistPriorityRule', 'plural');

    return html`
    </br>
      <x-checkbox
        ${this._workshopController.field('overrideInstitutionWaitinglistPriorityRules')}
        .label=${checkboxLabel}
        @value-picked=${(e: ValueEvent<boolean>) => {
          if (this._workshopController.document?.prices[0]?.unitAmount) {
            this._workshopController.document.overrideInstitutionWaitinglistPriorityRules = e.detail.value;
          }
          this.requestUpdate();
        }}
      ></x-checkbox>

      ${(() => {
        if (!this._workshopController.document?.overrideInstitutionWaitinglistPriorityRules) return nothing;
        return html`
          <bh-admin-waiting-list-priority-overview
            ${ref(this._waitingListPriorityOverview)}
            institution-id=${this.institutionID.value}
            product-id=${this.workshopID.value}
          ></bh-admin-waiting-list-priority-overview>
        `;
      })()}
    `;
  }

  private _saveWaitingListPriority() {
    return this._waitingListPriorityOverview.value?.saveAllPriorityRules(this._workshopController.document?._id);
  }

  private async saveWorkshop() {
    const e = this._workshopController.document;
    if (!e) return XSnackbar.show('Programmierfehler');
    await this._workshopController.validate();
    // await this._workshopController.validate({
    //   recurse: false,
    //   // aggregateErrors: true,
    //   // validateResolved: true,
    // });
  }

  private async deleteWorkshop() {
    const workshop = this._workshopController.document;
    if (!workshop) return XSnackbar.show('Programmierfehler');
    const institutionID = workshop.institutionID;
    const reallyDoIt = await XDialog.confirm('Sind Sie sicher, dass Sie das Workshop löschen möchten?');
    if (!reallyDoIt) return;
    const action = workshop.remove();
    await XDialog.waitFor(action).catch(this.e);
    goTo(`/admin/institution-details/${institutionID}/workshops`);
  }
}
